import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  addTableMaster,
  updateTableMaster,
  deleteTableMaster,
  countTableMaster,
  getTableMasterDetailById,
  getTableMasterList,
} from "../../api/UserTableapi";

const initialState = {
  tableMasters: [],
  currentTableMaster: null,
  totalCount: 0,
  loading: false,
  error: null,
  success: false,
  isFiltered: false,
};

// ─── Thunks ───────────────────────────────────────────────

export const fetchTableMasterList = createAsyncThunk(
  "tableMaster/getAll",
  async (payload = { page: 1, search: [] }, { rejectWithValue }) => {
    try {
      const response = await getTableMasterList(payload);
      return response.data?.data || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to fetch list"
      );
    }
  }
);

export const createTableMaster = createAsyncThunk(
  "tableMaster/create",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await addTableMaster(formData);
      return response.data?.data || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to create table master"
      );
    }
  }
);

export const editTableMaster = createAsyncThunk(
  "tableMaster/update",
  async ({ ROWID, data }, { rejectWithValue }) => {
    try {
      const response = await updateTableMaster(ROWID, data);
      return response.data?.data || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to update table master"
      );
    }
  }
);

export const removeTableMaster = createAsyncThunk(
  "tableMaster/delete",
  async (ROWID, { rejectWithValue }) => {
    try {
      const response = await deleteTableMaster(ROWID);
      return { ROWID };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to delete"
      );
    }
  }
);

export const getTableMasterCount = createAsyncThunk(
  "tableMaster/getCount",
  async (_, { rejectWithValue }) => {
    try {
      const response = await countTableMaster();
      return response.data?.count || 0;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to get count"
      );
    }
  }
);

export const fetchTableMasterById = createAsyncThunk(
  "tableMaster/getById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await getTableMasterDetailById(id);
      const data = response.data?.data;
      return Array.isArray(data) ? data[0] : data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to fetch table master details"
      );
    }
  }
);

// ─── Slice ────────────────────────────────────────────────

const tableMasterSlice = createSlice({
  name: "tableMaster",
  initialState,
  reducers: {
    resetTableMasterState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
    },
    clearCurrentTableMaster: (state) => {
      state.currentTableMaster = null;
    },
    clearTableMasters: (state) => {
      state.tableMasters = [];
    },
    setTableMasters: (state, action) => {
      state.tableMasters = action.payload;
    },
    setFilteredStatus: (state, action) => {
      state.isFiltered = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // ── Fetch All ──
      .addCase(fetchTableMasterList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTableMasterList.fulfilled, (state, action) => {
        state.loading = false;
        state.tableMasters = Array.isArray(action.payload) ? action.payload : [];
        state.error = null;
      })
      .addCase(fetchTableMasterList.rejected, (state, action) => {
        state.loading = false;
        state.tableMasters = [];
        state.error = action.payload;
      })

      // ── Create ──
      .addCase(createTableMaster.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createTableMaster.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(createTableMaster.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // ── Update ──
      .addCase(editTableMaster.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(editTableMaster.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(editTableMaster.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // ── Delete ──
      .addCase(removeTableMaster.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(removeTableMaster.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
        state.tableMasters = state.tableMasters.filter(
          (item) => String(item.ROWID) !== String(action.payload.ROWID)
        );
      })
      .addCase(removeTableMaster.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // ── Count ──
      .addCase(getTableMasterCount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTableMasterCount.fulfilled, (state, action) => {
        state.loading = false;
        state.totalCount = action.payload;
        state.error = null;
      })
      .addCase(getTableMasterCount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ── Fetch By ID ──
      .addCase(fetchTableMasterById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTableMasterById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTableMaster = action.payload;
        state.error = null;
      })
      .addCase(fetchTableMasterById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  resetTableMasterState,
  clearCurrentTableMaster,
  clearTableMasters,
  setTableMasters,
  setFilteredStatus,
} = tableMasterSlice.actions;

export const selectTableMasters = (state) => state.tableMaster.tableMasters;
export const selectCurrentTableMaster = (state) => state.tableMaster.currentTableMaster;
export const selectTotalCount = (state) => state.tableMaster.totalCount;
export const selectTableMasterLoading = (state) => state.tableMaster.loading;
export const selectTableMasterError = (state) => state.tableMaster.error;
export const selectTableMasterSuccess = (state) => state.tableMaster.success;

export default tableMasterSlice.reducer;